import React, { useState, useRef, useEffect } from "react";
import { api, apiError, mediaUrl, FEELINGS } from "../lib/api";
import { processImage } from "../lib/image";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmojiButton, FeelingButton } from "./EmojiFeeling";
import { ImagePlus, X, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 10;

export function StatusComposer({ onPosted }) {
  const { user, patchUser } = useAuth();
  const [text, setText] = useState("");
  const [feeling, setFeeling] = useState(null);
  const [posting, setPosting] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tagged, setTagged] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [addToPhotos, setAddToPhotos] = useState(false);
  const fileRef = useRef();
  const feelingObj = FEELINGS.find((f) => f.label === feeling);

  useEffect(() => {
    if (!tagQuery.trim()) { setTagResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(tagQuery)}`); setTagResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery]);

  const pickMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - media.length;
    if (room <= 0) { toast.error(`You can add up to ${MAX_PHOTOS} photos.`); return; }
    const batch = files.slice(0, room);
    setUploading(true);
    try {
      for (const file of batch) {
        const prepared = file.type.startsWith("image") ? await processImage(file) : file;
        const fd = new FormData();
        fd.append("file", prepared);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((prev) => [...prev, { url: data.url, type: file.type.startsWith("video") ? "video" : "image" }]);
      }
      if (files.length > room) toast.info(`Added ${room} — max is ${MAX_PHOTOS} photos.`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const tagAllPride = async () => {
    try {
      const { data } = await api.get("/friends");
      if (!data.length) { toast.info("You have no Pride to tag yet."); return; }
      setTagged(data.map((u) => ({ id: u.id, name: u.name, username: u.username })));
      toast.success(`Tagged all ${data.length} of your Pride`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!text.trim() && !feeling && media.length === 0) { toast.error("Say something, add a photo, or pick a feeling."); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/status", { text, feeling, post_to_feed: true, media, tagged_user_ids: tagged.map((t) => t.id) });
      patchUser({ status: data.status });
      const images = media.filter((m) => m.type !== "video");
      if (addToPhotos && images.length) {
        let added = 0;
        for (const m of images) {
          try { await api.post("/users/me/photos", { url: m.url }); added++; } catch (e) {}
        }
        if (added) toast.success(`Added ${added} photo${added > 1 ? "s" : ""} to your profile`);
      }
      setText(""); setFeeling(null); setMedia([]); setTagged([]); setTagQuery(""); setAddToPhotos(false);
      onPosted?.(data.post);
      toast.success("Status updated!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  return (
    <div className="bg-white border-4 border-black rounded-2xl p-4 brand-shadow" data-testid="status-composer">
      <div className={`flex ${user?.profile_frame ? "gap-6" : "gap-3"}`}>
        <Avatar user={user} size="md" />
        <div className="flex-1 space-y-2">
          <Textarea data-testid="status-text" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={`What's your status${feelingObj ? `, ${feelingObj.label}` : ""}?`}
            className="border-2 border-black resize-none min-h-[56px] text-base focus-visible:ring-spotter-gold" />

          {media.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="status-media-preview">
              {media.map((m, i) => (
                <div key={i} className="relative aspect-square rounded-lg border-2 border-black overflow-hidden bg-spotter-cream">
                  {m.type === "video"
                    ? <video src={mediaUrl(m.url)} className="w-full h-full object-cover" />
                    : <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                  <button data-testid={`status-remove-media-${i}`} onClick={() => setMedia(media.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}

          {media.some((m) => m.type !== "video") && (
            <label className="flex items-center gap-2 text-sm text-spotter-maroon font-semibold cursor-pointer" data-testid="status-add-to-photos-label">
              <input type="checkbox" data-testid="status-add-to-photos" checked={addToPhotos} onChange={(e) => setAddToPhotos(e.target.checked)} className="w-4 h-4 accent-spotter-maroon" />
              Also add {media.filter((m) => m.type !== "video").length > 1 ? "these photos" : "this photo"} to my profile photos
            </label>
          )}

          {tagged.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="status-tags">
              {tagged.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 bg-spotter-gold/30 border-2 border-black rounded-full px-2 py-0.5 text-xs font-bold">
                  @{t.username}
                  <button onClick={() => setTagged(tagged.filter((x) => x.id !== t.id))}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <button data-testid="status-clear-tags" onClick={() => setTagged([])} className="text-xs underline text-spotter-maroon">clear</button>
            </div>
          )}

          <div className="relative">
            <Input data-testid="status-tag-input" value={tagQuery} onChange={(e) => setTagQuery(e.target.value)} placeholder="Tag people…" className="border-2 border-black h-9" />
            {tagResults.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-white border-2 border-black rounded-lg overflow-hidden brand-shadow-sm">
                {tagResults.slice(0, 6).map((u) => (
                  <button key={u.id} data-testid={`status-tag-result-${u.id}`} onClick={() => { if (!tagged.find((x) => x.id === u.id)) setTagged([...tagged, u]); setTagQuery(""); setTagResults([]); }}
                    className="w-full flex items-center gap-2 p-2 hover:bg-spotter-cream text-left">
                    <Avatar user={u} size="sm" /><span className="text-sm font-semibold">{u.name}</span><span className="text-xs text-spotter-maroon">@{u.username}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={pickMedia} data-testid="status-file-input" />
          <div className="flex flex-wrap items-center gap-2">
            <EmojiButton onPick={(e) => setText((t) => t + e)} testid="status-emoji-btn" />
            <FeelingButton value={feeling} onPick={setFeeling} testid="status-feeling-btn" />
            <Button type="button" variant="outline" data-testid="status-add-media" onClick={() => fileRef.current?.click()} disabled={uploading || media.length >= MAX_PHOTOS}
              className="border-2 border-black gap-1.5 bg-white hover:bg-spotter-cream">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-spotter-gold" />}Photos
            </Button>
            <Button type="button" variant="outline" data-testid="status-tag-all-pride" onClick={tagAllPride}
              className="border-2 border-black gap-1.5 bg-white hover:bg-spotter-cream"><Users className="w-4 h-4 text-spotter-gold" />Tag all Pride</Button>
            <Button data-testid="status-submit" onClick={submit} disabled={posting || uploading}
              className="ml-auto bg-spotter-maroon text-spotter-cream font-heading font-bold border-2 border-black brand-shadow-sm">
              {posting ? "Sharing…" : "Update status"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
